import { useState, useEffect, useCallback, useRef } from 'react';

export interface CalorieGoal {
  userId: number;
  dailyCalorieGoal: number;
  carbsGoalPercent: number;
  proteinGoalPercent: number;
  fatGoalPercent: number;
}

export interface CalorieEntry {
  id?: number;
  userId: number;
  entryDate: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks';
  foodName: string;
  servingSize?: string;
  calories: number;
  carbsGrams?: number;
  proteinGrams?: number;
  fatGrams?: number;
  fiberGrams?: number;
  source?: 'camera' | 'text' | 'manual';
}

export interface DailySummary {
  date: string;
  totalCalories: number;
  totalCarbs: number;
  totalProtein: number;
  totalFat: number;
  totalFiber: number;
  meals: {
    breakfast: CalorieEntry[];
    lunch: CalorieEntry[];
    dinner: CalorieEntry[];
    snacks: CalorieEntry[];
  };
  goal: CalorieGoal | null;
}

export interface CalorieEntryInput {
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks';
  foodName: string;
  servingSize?: string;
  calories: number;
  carbsGrams?: number;
  proteinGrams?: number;
  fatGrams?: number;
  fiberGrams?: number;
  source?: 'camera' | 'text' | 'manual';
}

export interface UseCalorieTrackingReturn {
  goal: CalorieGoal | null;
  weekData: Map<string, DailySummary>;
  selectedDate: string;
  currentDaySummary: DailySummary | null;
  isLoading: boolean;
  needsSetup: boolean;
  weekOffset: number;
  error: string | null;
  setSelectedDate: (date: string) => void;
  saveGoal: (dailyCalorieGoal: number) => Promise<void>;
  addEntry: (entry: CalorieEntryInput) => Promise<number | undefined>;
  deleteEntry: (entryId: number) => Promise<void>;
  refetch: () => Promise<void>;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
}

const getToday = () => new Date().toISOString().split('T')[0];

function getWeekRange(offset: number): { startDate: string; endDate: string } {
  const today = new Date();
  const end = new Date(today);
  end.setDate(end.getDate() + offset * 7);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);

  // Cap endDate to today
  const cappedEnd = end > today ? today : end;

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: cappedEnd.toISOString().split('T')[0],
  };
}

export function useCalorieTracking(
  userId: string | undefined,
  token: string | undefined
): UseCalorieTrackingReturn {
  const [goal, setGoal] = useState<CalorieGoal | null>(null);
  const [weekData, setWeekData] = useState<Map<string, DailySummary>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string>(getToday());
  const [isLoading, setIsLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Guards against setState after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async (offset?: number) => {
    if (!userId || !token) {
      setIsLoading(false);
      return;
    }

    const currentOffset = offset !== undefined ? offset : weekOffset;
    const { startDate, endDate } = getWeekRange(currentOffset);

    try {
      setIsLoading(true);
      setError(null);

      // Fetch goal and week data in parallel
      const [goalResponse, weekResponse] = await Promise.all([
        fetch(`/api/chat/calorie-goal/${userId}?token=${token}`, {
          credentials: 'include',
        }),
        fetch(`/api/chat/calories/${userId}/week?token=${token}&startDate=${startDate}&endDate=${endDate}`, {
          credentials: 'include',
        }),
      ]);

      if (!isMountedRef.current) return;

      // Process goal
      if (goalResponse.ok) {
        const goalData = await goalResponse.json();
        if (!isMountedRef.current) return;
        if (goalData.success && goalData.data.hasGoal) {
          setGoal(goalData.data.goal);
          setNeedsSetup(false);
        } else {
          setGoal(null);
          setNeedsSetup(true);
        }
      } else {
        console.error(`[useCalorieTracking] Failed to fetch calorie goal (status ${goalResponse.status})`);
        setError(`Failed to fetch calorie goal (status ${goalResponse.status})`);
      }

      // Process week data
      if (weekResponse.ok) {
        const weekJson = await weekResponse.json();
        if (!isMountedRef.current) return;
        if (weekJson.success && weekJson.data.summaries) {
          const summariesMap = new Map<string, DailySummary>();
          Object.entries(weekJson.data.summaries).forEach(([date, summary]) => {
            summariesMap.set(date, summary as DailySummary);
          });
          setWeekData(summariesMap);
        }
      } else {
        console.error(`[useCalorieTracking] Failed to fetch week data (status ${weekResponse.status})`);
        setError(`Failed to fetch calorie data (status ${weekResponse.status})`);
      }
    } catch (err) {
      console.error('[useCalorieTracking] Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch calorie data');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [userId, token, weekOffset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const goToPreviousWeek = useCallback(() => {
    const newOffset = weekOffset - 1;
    setWeekOffset(newOffset);
    // Select the last day of the previous week
    const { endDate } = getWeekRange(newOffset);
    setSelectedDate(endDate);
  }, [weekOffset]);

  const goToNextWeek = useCallback(() => {
    if (weekOffset >= 0) return;
    const newOffset = weekOffset + 1;
    setWeekOffset(newOffset);
    // Select the first day of the next week, or today if current week
    if (newOffset === 0) {
      setSelectedDate(getToday());
    } else {
      const { startDate } = getWeekRange(newOffset);
      setSelectedDate(startDate);
    }
  }, [weekOffset]);

  const saveGoal = useCallback(
    async (dailyCalorieGoal: number) => {
      if (!userId || !token) return;

      try {
        const response = await fetch(`/api/chat/calorie-goal/${userId}?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            dailyCalorieGoal,
            carbsGoalPercent: 50,
            proteinGoalPercent: 25,
            fatGoalPercent: 25,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (!isMountedRef.current) return;
          if (data.success) {
            setGoal(data.data);
            setNeedsSetup(false);
            // Refetch week data to update goals in summaries
            await fetchData();
          } else {
            setError(data.error || 'Failed to save calorie goal');
          }
        } else {
          console.error(`[useCalorieTracking] Failed to save goal (status ${response.status})`);
          if (isMountedRef.current) {
            setError(`Failed to save calorie goal (status ${response.status})`);
          }
        }
      } catch (err) {
        console.error('[useCalorieTracking] Error saving goal:', err);
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to save calorie goal');
        }
        throw err;
      }
    },
    [userId, token, fetchData]
  );

  const addEntry = useCallback(
    async (entry: CalorieEntryInput): Promise<number | undefined> => {
      if (!userId || !token) return undefined;

      try {
        const response = await fetch(`/api/chat/calories/entry?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            entryDate: selectedDate,
            ...entry,
          }),
        });

        if (!response.ok) {
          console.error(`[useCalorieTracking] Failed to add entry (status ${response.status})`);
          if (isMountedRef.current) {
            setError(`Failed to add calorie entry (status ${response.status})`);
          }
          return undefined;
        }

        const data = await response.json();
        if (data.success) {
          const newEntryId: number | undefined = data.data?.id;
          if (!isMountedRef.current) return newEntryId;
          // Update local state immutably
          setWeekData((prev) => {
            const newMap = new Map(prev);
            const daySummary = newMap.get(selectedDate);
            if (daySummary) {
              const newEntry: CalorieEntry = {
                ...data.data,
                entryDate: selectedDate,
              };
              newMap.set(selectedDate, {
                ...daySummary,
                meals: {
                  ...daySummary.meals,
                  [entry.mealType]: [...daySummary.meals[entry.mealType], newEntry],
                },
                totalCalories: daySummary.totalCalories + entry.calories,
                totalCarbs: daySummary.totalCarbs + (entry.carbsGrams || 0),
                totalProtein: daySummary.totalProtein + (entry.proteinGrams || 0),
                totalFat: daySummary.totalFat + (entry.fatGrams || 0),
                totalFiber: daySummary.totalFiber + (entry.fiberGrams || 0),
              });
            }
            return newMap;
          });
          return newEntryId;
        }

        if (isMountedRef.current) {
          setError(data.error || 'Failed to add calorie entry');
        }
        return undefined;
      } catch (err) {
        console.error('[useCalorieTracking] Error adding entry:', err);
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to add calorie entry');
        }
        throw err;
      }
    },
    [userId, token, selectedDate]
  );

  const deleteEntry = useCallback(
    async (entryId: number) => {
      if (!userId || !token) return;

      try {
        const response = await fetch(`/api/chat/calories/entry/${entryId}?token=${token}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ userId }),
        });

        if (response.ok) {
          // Refetch to get updated data
          await fetchData();
        } else {
          console.error(`[useCalorieTracking] Failed to delete entry (status ${response.status})`);
          if (isMountedRef.current) {
            setError(`Failed to delete calorie entry (status ${response.status})`);
          }
        }
      } catch (err) {
        console.error('[useCalorieTracking] Error deleting entry:', err);
        if (isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to delete calorie entry');
        }
        throw err;
      }
    },
    [userId, token, fetchData]
  );

  const currentDaySummary = weekData.get(selectedDate) || null;

  return {
    goal,
    weekData,
    selectedDate,
    currentDaySummary,
    isLoading,
    needsSetup,
    weekOffset,
    error,
    setSelectedDate,
    saveGoal,
    addEntry,
    deleteEntry,
    refetch: fetchData,
    goToPreviousWeek,
    goToNextWeek,
  };
}
